import {ChangeDetectionStrategy, Component, computed, DestroyRef, Injectable, Input, signal, Signal} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Temporal} from "temporal-polyfill";
import {AksjeeierEnkelModell, Aksjeeiertype, EnkelGruppeMedlemModell, GruppeMedlemListeModell, GruppeRessursClient, MedlemsRolleModell} from "../../../../../felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {ControlContainer, FormControl, FormGroup, FormsModule, NgForm, ReactiveFormsModule} from "@angular/forms";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatRadioModule} from "@angular/material/radio";
import {MatIconModule} from "@angular/material/icon";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {AksjeeierDatatjeneste} from "felles-ng2/tjenester/datalager/AksjeeierDatatjeneste";
import {Observable} from "rxjs";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {MatOptionModule, MatOptionSelectionChange} from "@angular/material/core";
import {MatSelectModule} from "@angular/material/select";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {TranslateModule} from "@ngx-translate/core";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {eksistererMedlemMedEpost, eksistererMedlemMedNavn, medlemmerIkkeAnonymisert, primærMedlemsrolle, ValiderEpostRespons} from "felles-ng2/modell/medlemModellHjelpere";
import {lesPlainDateSerialisertOpt} from "felles-ng2/støttefunksjoner/temporal";
import {MedlemInvitasjonSkjemaKomponent, MedlemInvitasjonSkjemaKomponentAvhengigheter, MedlemInvitasjonSkjemaKomponentAvhengigheter_DI_TOKEN} from "web-ng2/moduler/medlem/komponenter/medlem-invitasjon-skjema/medlem-invitasjon-skjema";
import {finnAksjeeierRolle} from "felles-ng2/modell/gruppeModellHjelpere";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatTooltip} from "@angular/material/tooltip";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('AksjeeierRedigerKomponent');


export type SkjemaType = ReturnType<typeof AksjeeierRedigerKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];

@Component({
    selector: 'aksjeeier-rediger',
    templateUrl: './aksjeeier-rediger.html',
    styleUrls: ['./aksjeeier-rediger.scss'],
    imports: [
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatRadioModule,
        MatIconModule,
        MatDatepickerModule,
        ReactiveFormsModule,
        TranslateModule,
        MatOptionModule,
        MatSelectModule,
        StandardValideringsfeilPipe,
        MedlemInvitasjonSkjemaKomponent,
        TipsKomponent,
        MatSlideToggle,
        MatTooltip
    ],
    providers: [
        { provide: MedlemInvitasjonSkjemaKomponentAvhengigheter_DI_TOKEN, useExisting: AksjeeierRedigerKomponent }
    ],
    //normalt vil ikke skjema-elementer se etter et Form utenfor sin forelderkomp, sørger her for at vi kan nå Form til komp som bruker AksjeeierRedigerKomponent
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AksjeeierRedigerKomponent implements MedlemInvitasjonSkjemaKomponentAvhengigheter {

    /** Skjemadefinisjon, merk at vi ikke bruker formbuilder pga at enkelte felter må def med `nonNullable: true`*/
    static opprettSkjema() {
        const nyInvitasjonSkjema = MedlemInvitasjonSkjemaKomponent.opprettSkjema();
        nyInvitasjonSkjema.disable();
        return new FormGroup({
            id: new FormControl('', {nonNullable: true}), //ikke eksponert for redigering
            type: new FormControl(Aksjeeiertype.person),
            navn: new FormControl('', {nonNullable: true, validators: FeltValidatorer.litenIkkeTomStreng}),
            adresse: new FormControl('', {nonNullable: true, validators: FeltValidatorer.storStreng}),
            epost: new FormControl('', {nonNullable: false, validators: FeltValidatorer.epost}),
            foedselsdato: new FormControl<PlainDate | null>(null),
            organisasjonsnummer: new FormControl('', FeltValidatorer.litenStreng),
            representertAv:  new FormControl<EnkelGruppeMedlemModell | null>(null, []),
            erNyInvitasjon: new FormControl(false, {nonNullable: true}),
            nyInvitasjonSkjema: nyInvitasjonSkjema,
            sendInvitasjon: new FormControl(false, {nonNullable: true})
        });
    }

    static aksjeeierEnkelModellSomSkjemaVerdier(aksjeeier: AksjeeierEnkelModell): SkjemaVerdier {
        return {...aksjeeier, foedselsdato: lesPlainDateSerialisertOpt(aksjeeier.foedselsdato), erNyInvitasjon: false};
    }

    static nyTomPerson(): SkjemaVerdier {
        return {
            id: nyUuid(),
            type: Aksjeeiertype.person,
            navn: '',
            adresse: '',
            epost: '',
            foedselsdato: null,
            organisasjonsnummer: null,
            representertAv: null,
            erNyInvitasjon: false,
            sendInvitasjon: false
        };
    };

    aksjeeiertypePerson = Aksjeeiertype.person
    aksjeeiertypeOrganisasjon = Aksjeeiertype.organisasjon
    duplikatAksjeeier = computed(() => this.finnDuplikat());

    /** navn-feltets verdi, oppdateres når bruker redigerer feltet. */
    private navnVerdi = signal('');
    private eksisterendeAksjeeiere: Signal<AksjeeierEnkelModell[]>;

    // Lastes via komponentavhengigheter
    medlemmer:GruppeMedlemListeModell[] = [];

    /** Settes i  konstruktør */
    private _medlemsroller : Observable<MedlemsRolleModell[]>;

    standardAksjeeierRolle = signal<MedlemsRolleModell | null>(null);

    private _skjema!: SkjemaType;

    constructor(avhengigheter: AksjeeierRedigerKomponentAvhengigheter, private destroyRef: DestroyRef) {
        this.eksisterendeAksjeeiere = toSignal(avhengigheter.eksisterendeAksjeeiere(), {initialValue: []});
        this.medlemmer = avhengigheter.gruppemedlemmer();
        this._medlemsroller = avhengigheter.lastMedlemsroller();
        this._medlemsroller.subscribe(roller =>
          this.standardAksjeeierRolle.set(finnAksjeeierRolle(roller))
        )
    }

    @Input({required: true}) set skjema(skjema: SkjemaType) {
        this._skjema = skjema;
        this.navnVerdi.set(this.skjema.value.navn ?? ''); //sikrer trigging av validering også før feltet er redigert av bruker
        this.skjema.controls.navn.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(navn => this.navnVerdi.set(navn));
    }

    get skjema() {
        return this._skjema;
    }

    erValgtMedlem(a: GruppeMedlemListeModell | null, b: GruppeMedlemListeModell | null) {
        debug('erValgtMedlem', a, b)
        if (a === b) return true;
        if (erNil(a) || erNil(b)) return false;
        return a.id === b.id;
    }

    aktiveRolleMedParantesRundt(medlem: GruppeMedlemListeModell) {
        const rolle = primærMedlemsrolle(medlem);
        if (rolle == null) return '';
        return '(' + rolle.tittel +')';
    }

    visNyInvitasjonSkjema($event: MatOptionSelectionChange) {
        if ($event.isUserInput) {
            this.skjema.controls.nyInvitasjonSkjema.enable();
            this.skjema.patchValue({
                erNyInvitasjon: true,
                nyInvitasjonSkjema: {
                    id: nyUuid(),
                    navn: this.skjema.value.type == Aksjeeiertype.person ? (this.skjema.value.navn ?? null) : null,
                    epost: this.skjema.value.epost ?? '',
                    mobilnummer: null,
                    rolle: this.standardAksjeeierRolle()
                }
            });
        } else {
            this.kansellerNyInvitasjonSkjema();
        }
    }

    vedAvbrytNyMedlem() {
        this.skjema.controls.representertAv.setValue(null);
        this.kansellerNyInvitasjonSkjema();
    }

    // MedlemInvitasjonSkjemaKomponentAvhengigheter implementasjon

    medlemsroller(): Observable<MedlemsRolleModell[]> {
        return this._medlemsroller;
    }

    verifiserAtEpostIkkeAlleredeErRegistrert(epost: string): ValiderEpostRespons {
        return eksistererMedlemMedEpost(this.somNullsikkerGruppemedlemmer(), epost);
    }

    /** Sann dersom det ikke finnes et medlem med samme navn (casing ignorert), ignorer permanent deaktiverte medlemmer */
    verifiserAtNavnIkkeAlleredeErRegistrert(navn: string): boolean {
        return eksistererMedlemMedNavn(medlemmerIkkeAnonymisert(this.medlemmer), navn);
    }


    //hjelpmetoder

    private kansellerNyInvitasjonSkjema() {
        this.skjema.patchValue({erNyInvitasjon: false});
        this.skjema.controls.nyInvitasjonSkjema.disable();
    }

    private somNullsikkerGruppemedlemmer() {
        return this.medlemmer ?? [];
    }

    private finnDuplikat() {
        debug('finnDuplikat')
        return første(this.eksisterendeAksjeeiere()
          .filter(eksisterende =>
            eksisterende.navn.toLocaleLowerCase() == this.navnVerdi().toLocaleLowerCase() &&
            eksisterende.id != this.skjema.value.id)) //utelat aksjeeieren vi redigerer
    }
}


@Injectable()
export class AksjeeierRedigerKomponentAvhengigheter {

    constructor(private aksjeeierDatatjeneste: AksjeeierDatatjeneste, private gruppeDatatjeneste: GruppeDatatjeneste, private gruppeRessurs: GruppeRessursClient) {
    }

    eksisterendeAksjeeiere(): Observable<AksjeeierEnkelModell[]> {
        return this.aksjeeierDatatjeneste.finnAksjeeiereTilknyttet(this.aksjeeierDatatjeneste.aktivAksjeeierbokId!);
    }

    gruppemedlemmer(): GruppeMedlemListeModell[] {
        //merk at vi forventer at aktivGruppe er satt via annen kode
        return this.gruppeDatatjeneste.aktivGruppeIkkeNull().medlemmer
    }

    lastMedlemsroller(): Observable<MedlemsRolleModell[]> {
        return this.gruppeRessurs.finnTilgjengeligeRoller(this.gruppeDatatjeneste.aktivGruppeIkkeNull().id).somObservable();
    }

}
